import type { Topic } from "@/types/content";

interface FilterBarProps {
  topics: Topic[];
  activeCategory: string;
  onCategory: (slug: string) => void;
  schools: string[];
  activeSchool: string;
  onSchool: (school: string) => void;
  sort: string;
  onSort: (sort: string) => void;
  keyword: string;
  onKeyword: (keyword: string) => void;
  resultCount: number;
}

const sortOptions = [
  { value: "hot", label: "最热" },
  { value: "new", label: "最新" },
  { value: "discuss", label: "讨论最多" },
];

export default function FilterBar({
  topics,
  activeCategory,
  onCategory,
  schools,
  activeSchool,
  onSchool,
  sort,
  onSort,
  keyword,
  onKeyword,
  resultCount,
}: FilterBarProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex h-11 flex-1 items-center gap-2.5 rounded-full border border-background-300 bg-background-100 px-5 lg:max-w-md">
          <i className="ri-search-line text-base text-foreground-500"></i>
          <input
            type="search"
            value={keyword}
            onChange={(e) => onKeyword(e.target.value)}
            placeholder="搜索帖子标题、学校或关键词…"
            aria-label="在讨论区中搜索"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label
              htmlFor="school-filter"
              className="text-xs text-foreground-600 md:text-sm whitespace-nowrap"
            >
              学校
            </label>
            <select
              id="school-filter"
              value={activeSchool}
              onChange={(e) => onSchool(e.target.value)}
              className="h-10 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            >
              <option value="">全部学校</option>
              {schools.map((school) => (
                <option key={school} value={school}>
                  {school}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center rounded-full border border-background-300 bg-background-100 p-1">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onSort(option.value)}
                aria-pressed={sort === option.value}
                className={`cursor-pointer rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors whitespace-nowrap md:text-sm ${
                  sort === option.value
                    ? "bg-foreground-950 text-background-50"
                    : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-background-200 pt-4">
        <button
          type="button"
          onClick={() => onCategory("")}
          aria-pressed={activeCategory === ""}
          className={`cursor-pointer rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors whitespace-nowrap md:text-sm ${
            activeCategory === ""
              ? "bg-primary-500 text-background-50 dark:text-foreground-950"
              : "bg-background-100 text-foreground-700 hover:bg-background-200"
          }`}
        >
          全部板块
        </button>
        {topics.map((topic) => (
          <button
            key={topic.slug}
            type="button"
            onClick={() => onCategory(topic.slug)}
            aria-pressed={activeCategory === topic.slug}
            className={`flex cursor-pointer items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors whitespace-nowrap md:text-sm ${
              activeCategory === topic.slug
                ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={topic.icon}></i>
            </span>
            {topic.name}
          </button>
        ))}
        <span className="ml-auto text-xs text-foreground-500 md:text-sm">
          共 {resultCount} 篇讨论
        </span>
      </div>
    </div>
  );
}